import PropTypes from 'prop-types';
/* eslint new-cap: ["error", { "capIsNewExceptions": ["HOC"] }] */
import React from 'react';
import CardField from './CardFieldNoValidate';
import styles from './CardField.scss';
import moment from 'moment-timezone';
import DatePicker from './DatePicker';
import { Dropdown } from 'semantic-ui-react';
import { HOC } from 'formsy-react';

const DateTimePicker = class DateTimePicker extends React.Component {
  static propTypes = {
    value: PropTypes.string,
    dateOnly: PropTypes.bool,
    datePlaceholder: PropTypes.string,
    timePlaceholder: PropTypes.string,
    timeZone: PropTypes.string,
    onChange: PropTypes.func,
    viewer: PropTypes.object,
    handleBlurringDatePicker: PropTypes.func,
    setValue: PropTypes.func,
    getValue: PropTypes.func
  };

  constructor(props) {
    super(props);
    const timeZone = this.props.timeZone || moment.tz.guess();
    this.state = {
      datepickerOpen: false,
      time: moment(this.props.getValue()).isValid()
        ? moment(this.props.getValue()).tz(timeZone).format('LT')
        : '',
      timeView: moment(this.props.getValue()).isValid()
        ? moment(this.props.getValue()).tz(timeZone).format('LT')
        : '',
      date: moment(this.props.getValue()).isValid()
        ? this.getDateString(
            this.props.getValue(),
            this.props.dateOnly,
            timeZone
          )
        : '',
      month: moment.tz(timeZone).toDate()
    };
    this.onDayClick = this.onDayClick.bind(this);
    this.onDateInputChange = this.onDateInputChange.bind(this);
    this.onFocus = this.onFocus.bind(this);
    this.showCurrentDate = this.showCurrentDate.bind(this);
    this.onBlur = this.onBlur.bind(this);
    this.updateSelectedDay = this.updateSelectedDay.bind(this);
    this.updateDateTime = this.updateDateTime.bind(this);
    this.getIso8601Date = this.getIso8601Date.bind(this);
    this.closeDatePicker = this.closeDatePicker.bind(this);
    this.onTimeInputChange = this.onTimeInputChange.bind(this);
    this.onTimeSelectChange = this.onTimeSelectChange.bind(this);
    this.onTimeInputBlur = this.onTimeInputBlur.bind(this);
    this.onDateTimePickerBlur = this.onDateTimePickerBlur.bind(this);
  }

  componentWillReceiveProps(nextProps) {
    const nextValue = nextProps.value;
    const timeZone = nextProps.timeZone || moment.tz.guess();
    if (
      nextProps.dateOnly !== this.props.dateOnly &&
      moment(nextValue, moment.ISO_8601).isValid()
    ) {
      // Update date in local state based on changes in mobile datepicker component
      this.setState({
        date: this.getDateString(nextValue, nextProps.dateOnly, timeZone),
        time: moment(nextValue).tz(timeZone).format('LT')
      });
    }
    if (
      (nextValue !== this.props.value ||
        (!nextProps.dateOnly && timeZone !== this.props.timeZone)) &&
      moment(nextValue, moment.ISO_8601).isValid()
    ) {
      this.setState({
        date: this.getDateString(nextValue, nextProps.dateOnly, timeZone),
        time: moment.tz(nextValue, timeZone).format('LT'),
        timeView: moment.tz(nextValue, timeZone).format('LT')
      });
    }
  }

  onFocus() {
    this.setState({ datepickerOpen: true });
    this.showCurrentDate();
  }

  onBlur(e) {
    const { value } = e.target;
    const timeZone = this.props.timeZone || moment.tz.guess();
    const formattedDate = this.parseDate(value);

    // Change the current month only if the value entered by the user
    // is a valid date, according to the `L` format
    if (moment(formattedDate, 'L', true).isValid()) {
      this.setState(
        {
          month: moment.tz(formattedDate, 'L', timeZone).toDate(),
          date: formattedDate
        },
        () => {
          this.showCurrentDate();
          this.changeValue({
            target: {
              value: this.getIso8601Date({ dateOnly: this.props.dateOnly })
            }
          });
        }
      );
    } else {
      this.setState({ date: '' }, this.showCurrentDate);
    }
    if (e.relatedTarget && !e.relatedTarget.className.match('DayPicker')) {
      this.closeDatePicker();
    }
  }

  onDateTimePickerBlur(e) {
    // Determine if blurring off DateTimePicker completely
    // or moving focusing onto DayPicker, which we still
    // consider part of this component
    if (e.relatedTarget && e.relatedTarget.className.match('DayPicker-Day')) {
      this.handleBlurringDatePicker(false);
    } else {
      this.handleBlurringDatePicker(true);
    }
  }

  onTimeInputChange(e) {
    const { value } = e.currentTarget;
    this.setState({ timeView: value });
  }

  onTimeInputBlur(e) {
    const { value } = e.target;
    const formattedTime = this.parseTime(value);

    if (formattedTime) {
      this.setState(
        {
          time: formattedTime,
          timeView: formattedTime
        },
        () => {
          this.handleBlurringDatePicker(true);
          this.changeValue({
            target: {
              value: this.getIso8601Date({ dateOnly: this.props.dateOnly })
            }
          });
        }
      );
    } else {
      this.setState({ time: '', timeView: '' });
    }
  }

  onTimeSelectChange(e, change) {
    e.stopPropagation();
    if (e.target.value === undefined) {
      this.setState(
        {
          time: change.value,
          timeView: change.value
        },
        () => {
          this.changeValue({
            target: {
              value: this.getIso8601Date({ dateOnly: this.props.dateOnly })
            }
          });
        }
      );
    }
  }

  onDateInputChange(e) {
    const { value } = e.target;
    e.stopPropagation();

    // Change the current month only if the value entered by the user
    // is a valid date, according to the `L` format
    if (moment(value, 'L', true).isValid()) {
      this.setState(
        {
          month: moment(value, 'L').toDate(),
          date: value
        },
        () => {
          this.showCurrentDate();
          this.changeValue({
            target: {
              value: this.getIso8601Date({ dateOnly: this.props.dateOnly })
            }
          });
        }
      );
    } else {
      this.setState({ date: value }, this.showCurrentDate);
    }
  }

  onDayClick(e, day) {
    this.setState({
      date: moment(day).format('L'),
      month: day
    });
  }

  getDateString(date, dateOnly, timeZone) {
    if (dateOnly) {
      return moment(date).format('L');
    }
    return moment.tz(date, timeZone).format('L');
  }

  getIso8601Date(options) {
    const { dateOnly } = options;
    const timeZone = this.props.timeZone || moment.tz.guess();
    if (dateOnly) {
      if (moment(this.state.date, 'L').isValid()) {
        return moment(this.state.date, 'L').format('YYYY-MM-DD');
      }
    } else {
      if (moment(this.state.date, 'L LT').isValid()) {
        return moment
          .tz(`${this.state.date} ${this.state.time}`, 'L LT', timeZone)
          .toISOString();
      }
    }
    return '';
  }

  handleBlurringDatePicker(blurring) {
    if (this.props.handleBlurringDatePicker) {
      if (
        (!this.props.dateOnly &&
          this.state.date !== '' &&
          this.state.time !== '') ||
        (this.props.dateOnly && this.state.date !== '')
      ) {
        this.props.handleBlurringDatePicker(blurring);
      } else if (blurring) {
        this.props.handleBlurringDatePicker(true);
      } else {
        this.props.handleBlurringDatePicker(false);
      }
    }
  }

  changeValue(e) {
    if (this.props.dateOnly) {
      if (this.state.date) {
        this.props.setValue(e.target.value);
        this.props.onChange(e);
      }
    } else {
      if (this.state.date && this.state.time) {
        this.props.setValue(e.target.value);
        this.props.onChange(e);
      }
    }
  }

  closeDatePicker() {
    this.setState({ datepickerOpen: false });
    this.handleBlurringDatePicker(true);
  }

  nearestHalfHour(time) {
    const minutes = moment(time).minutes();
    let momentTime = null;
    if (minutes >= 0 && minutes < 15) {
      momentTime = moment(time).minutes(0);
    } else if (minutes >= 15 && minutes < 45) {
      momentTime = moment(time).minutes(30);
    } else {
      momentTime = moment(time).minutes(0).add(1, 'hour');
    }
    return momentTime.format('LT');
  }

  parseTime(timeString) {
    // do parsing here and return appropriate value
    const timeParts = timeString.match(
      /([0-9]{1,2}):?([0-9]{1,2})?\s?([AaPp][Mm]?)?/
    );
    if (!timeParts) {
      return null;
    }
    const hours = parseInt(timeParts[1], 10);
    const minutes = parseInt(timeParts[2] || '0', 10);
    let hoursString = '';
    let minutesString = timeParts[2] || '00';
    let meridian = '';
    if (hours > 0 && hours <= 12) {
      hoursString = timeParts[1];
    } else {
      // Out of range
      return null;
    }
    if (minutes >= 0 && minutes <= 60) {
      while (minutesString.length < 2) {
        minutesString = `0${minutesString}`;
      }
    } else {
      // Out of range
      return null;
    }
    if (timeParts[3]) {
      meridian =
        timeParts[3].length === 1
          ? `${timeParts[3].toUpperCase()}M`
          : timeParts[3].toUpperCase();
    } else {
      meridian = hours > 0 && hours < 7 ? 'AM' : 'PM';
    }

    return `${hoursString}:${minutesString} ${meridian}`;
  }

  parseDate(date) {
    const dateParts = date.split('/');
    if (dateParts.length === 3) {
      if (dateParts[0].length === 1) {
        dateParts[0] = `0${dateParts[0]}`;
      }
      if (dateParts[1].length === 1) {
        dateParts[1] = `0${dateParts[1]}`;
      }
      if (dateParts[2].length === 2) {
        dateParts[2] =
          parseInt(new Date().getFullYear() / 100, 10) + dateParts[2];
      }
      return dateParts.join('/');
    }
    return date;
  }

  showCurrentDate() {
    // TODO: fix refs to call showMonth on child component
    this.setState({ month: this.state.date });
    // this.refs.daypicker.showMonth(this.state.month);
  }

  updateSelectedDay(date) {
    this.setState({ date: moment(date).format('L') }, () => {
      this.changeValue({
        target: {
          value: this.getIso8601Date({ dateOnly: this.props.dateOnly })
        }
      });
    });
  }

  updateDateTime() {
    this.changeValue({
      target: { value: this.getIso8601Date({ dateOnly: this.props.dateOnly }) }
    });
  }

  render() {
    const times = [
      { text: '12:00 AM', value: '12:00 AM' },
      { text: '12:30 AM', value: '12:30 AM' },
      { text: '1:00 AM', value: '1:00 AM' },
      { text: '1:30 AM', value: '1:30 AM' },
      { text: '2:00 AM', value: '2:00 AM' },
      { text: '2:30 AM', value: '2:30 AM' },
      { text: '3:00 AM', value: '3:00 AM' },
      { text: '3:30 AM', value: '3:30 AM' },
      { text: '4:00 AM', value: '4:00 AM' },
      { text: '4:30 AM', value: '4:30 AM' },
      { text: '5:00 AM', value: '5:00 AM' },
      { text: '5:30 AM', value: '5:30 AM' },
      { text: '6:00 AM', value: '6:00 AM' },
      { text: '6:30 AM', value: '6:30 AM' },
      { text: '7:00 AM', value: '7:00 AM' },
      { text: '7:30 AM', value: '7:30 AM' },
      { text: '8:00 AM', value: '8:00 AM' },
      { text: '8:30 AM', value: '8:30 AM' },
      { text: '9:00 AM', value: '9:00 AM' },
      { text: '9:30 AM', value: '9:30 AM' },
      { text: '10:00 AM', value: '10:00 AM' },
      { text: '10:30 AM', value: '10:30 AM' },
      { text: '11:00 AM', value: '11:00 AM' },
      { text: '11:30 AM', value: '11:30 AM' },
      { text: '12:00 PM', value: '12:00 PM' },
      { text: '12:30 PM', value: '12:30 PM' },
      { text: '1:00 PM', value: '1:00 PM' },
      { text: '1:30 PM', value: '1:30 PM' },
      { text: '2:00 PM', value: '2:00 PM' },
      { text: '2:30 PM', value: '2:30 PM' },
      { text: '3:00 PM', value: '3:00 PM' },
      { text: '3:30 PM', value: '3:30 PM' },
      { text: '4:00 PM', value: '4:00 PM' },
      { text: '4:30 PM', value: '4:30 PM' },
      { text: '5:00 PM', value: '5:00 PM' },
      { text: '5:30 PM', value: '5:30 PM' },
      { text: '6:00 PM', value: '6:00 PM' },
      { text: '6:30 PM', value: '6:30 PM' },
      { text: '7:00 PM', value: '7:00 PM' },
      { text: '7:30 PM', value: '7:30 PM' },
      { text: '8:00 PM', value: '8:00 PM' },
      { text: '8:30 PM', value: '8:30 PM' },
      { text: '9:00 PM', value: '9:00 PM' },
      { text: '9:30 PM', value: '9:30 PM' },
      { text: '10:00 PM', value: '10:00 PM' },
      { text: '10:30 PM', value: '10:30 PM' },
      { text: '11:00 PM', value: '11:00 PM' },
      { text: '11:30 PM', value: '11:30 PM' }
    ];

    const selectedDay = this.state.date
      ? new Date(moment(this.state.date, 'L', true).toDate())
      : new Date();

    const input = (
      <div style={{ display: 'flex', flex: 1 }}>
        <input
          type='text'
          style={{
            border: 'none',
            outline: 'none',
            background: 'transparent',
            padding: '0',
            flex: 1
          }}
          className='ignore-react-onclickoutside'
          value={this.state.date}
          placeholder={this.props.datePlaceholder || 'Date'}
          onBlur={this.onBlur}
          onChange={this.onDateInputChange}
          onFocus={this.onFocus}
        />
        {this.state.datepickerOpen &&
          <DatePicker
            close={this.closeDatePicker}
            style={{ flex: 1 }}
            initialMonth={this.state.month}
            selectedDay={selectedDay}
            updateSelectedDay={this.updateSelectedDay}
            onDayClick={this.onDayClick}
            viewer={this.props.viewer}
          />}
      </div>
    );

    const timeInput = (
      <input
        type='text'
        style={{
          border: 'none',
          outline: 'none',
          background: 'transparent',
          padding: '0'
        }}
        className='search'
        value={this.state.timeView}
        onBlur={this.onTimeInputBlur}
        onChange={this.onTimeInputChange}
        placeholder={this.props.timePlaceholder || 'Time'}
      />
    );

    const timeSelect =
      !this.props.dateOnly &&
        <Dropdown
          inline
          basic
          fluid
          trigger={timeInput}
          placeholder={this.props.timePlaceholder || 'Time'}
          scrolling
          options={times}
          style={{ display: 'flex', flex: 1, marginTop: '-1px' }}
          value={this.nearestHalfHour(
            new Date(this.getIso8601Date({ dateOnly: false }))
          )}
          onChange={this.onTimeSelectChange}
          tabIndex={-1}
        />;

    return (
      <CardField {...this.props} onBlur={this.onDateTimePickerBlur}>
        <div className={styles.datetimeOffset} style={{ display: 'flex' }}>
          {input}
          {timeSelect}
        </div>
      </CardField>
    );
  }
};

export default HOC(DateTimePicker);



// WEBPACK FOOTER //
// ./webpack/components/Elements/CardField/CardDateTimePicker.js